package org.example.project.enginetabular

import org.example.project.utils.All

internal fun creatorDocumentResult(document: All, statusText: String): All =
    All(module = listOf(document), bemerkung = statusText)

internal fun creatorResultDocument(result: All): All = result.module.firstOrNull() ?: createEmptyDocument()

internal fun creatorResultStatus(result: All): String = result.bemerkung

internal fun createSizedDocument(
    name: String,
    rows: Int,
    cols: Int
): All {
    val document = TabularManager.createEmptyTable(
        name = name,
        headers = List(cols) { "" }
    )
    val rowData = TabularManager.normalizeRows(
        List(rows) { All(columns = List(document.columnSettings.size) { "" }) },
        document.columnSettings.size
    )
    return document.copy(module = rowData)
}

internal fun createEmptyDocument(name: String = "Neue Tabelle"): All =
    TabularManager.createEmptyTable(name = name)

internal fun ensureRectangular(document: All): All {
    if (document.columnSettings.isEmpty() && document.module.isEmpty()) return document
    val columns = if (document.columnSettings.isEmpty()) {
        TabularManager.normalizeColumns(listOf(All(key = "", label = "")))
    } else {
        TabularManager.normalizeColumns(document.columnSettings)
    }
    val rows = TabularManager.normalizeRows(document.module, columns.size.coerceAtLeast(1))
    return document.copy(columnSettings = columns, module = rows)
}

internal fun creatorSelectionText(document: All, range: TabularCellRange): String =
    buildString {
        for (rowIndex in range.topRow..range.bottomRow) {
            val row = document.module.getOrNull(rowIndex)?.columns.orEmpty()
            val columns = (range.leftCol..range.rightCol).map { col -> row.getOrNull(col).orEmpty() }
            append(columns.joinToString("\t"))
            if (rowIndex < range.bottomRow) append('\n')
        }
    }

internal fun ensureCreatorCapacity(
    document: All,
    requiredCols: Int = document.columnSettings.size,
    requiredRows: Int = document.module.size
): All {
    val normalized = ensureRectangular(document)
    val targetCols = maxOf(normalized.columnSettings.size, requiredCols)
    val targetRows = maxOf(normalized.module.size, requiredRows)
    val columns = if (targetCols > normalized.columnSettings.size) {
        TabularManager.normalizeColumns(
            normalized.columnSettings + List(targetCols - normalized.columnSettings.size) { All(key = "", label = "") }
        )
    } else {
        normalized.columnSettings
    }
    val rows = TabularManager.normalizeRows(
        normalized.module + List((targetRows - normalized.module.size).coerceAtLeast(0)) { All(columns = emptyList()) },
        columns.size
    )
    return normalized.copy(columnSettings = columns, module = rows)
}

internal fun applyCreatorBlock(
    document: All,
    startRow: Int,
    startCol: Int,
    block: List<List<String>>
): All {
    if (block.isEmpty()) return ensureRectangular(document)
    val prepared = ensureCreatorCapacity(
        document = document,
        requiredCols = startCol + (block.maxOfOrNull { it.size } ?: 0),
        requiredRows = startRow + block.size
    )
    val rows = prepared.module.toMutableList()
    block.forEachIndexed { rowOffset, values ->
        val rowIndex = startRow + rowOffset
        val currentRow = rows[rowIndex]
        val updated = currentRow.columns.toMutableList()
        values.forEachIndexed { colOffset, value ->
            val colIndex = startCol + colOffset
            if (colIndex in updated.indices) updated[colIndex] = value
        }
        rows[rowIndex] = currentRow.copy(columns = updated)
    }
    return prepared.copy(module = rows)
}

internal fun clearCreatorRange(document: All, range: TabularCellRange): All {
    val prepared = ensureRectangular(document)
    val rows = prepared.module.toMutableList()
    for (rowIndex in range.topRow..range.bottomRow) {
        val currentRow = rows.getOrNull(rowIndex) ?: continue
        val updated = currentRow.columns.toMutableList()
        for (colIndex in range.leftCol..range.rightCol) {
            if (colIndex in updated.indices) updated[colIndex] = ""
        }
        rows[rowIndex] = currentRow.copy(columns = updated)
    }
    return prepared.copy(module = rows)
}

internal fun appendCreatorRows(document: All, count: Int): All {
    val normalized = ensureRectangular(document)
    return creatorDocumentResult(
        document = ensureCreatorCapacity(normalized, requiredRows = normalized.module.size + count),
        statusText = "$count Zeile(n) hinzugefügt."
    )
}

internal fun appendCreatorColumns(document: All, count: Int): All {
    val normalized = ensureRectangular(document)
    return creatorDocumentResult(
        document = ensureCreatorCapacity(normalized, requiredCols = normalized.columnSettings.size + count),
        statusText = "$count Spalte(n) hinzugefügt."
    )
}

internal fun removeCreatorRows(document: All, count: Int): All {
    val normalized = ensureRectangular(document)
    val removeCount = count.coerceAtMost(normalized.module.size)
    return creatorDocumentResult(
        document = normalized.copy(module = normalized.module.dropLast(removeCount)),
        statusText = "$removeCount Zeile(n) entfernt."
    )
}

internal fun removeCreatorColumns(document: All, count: Int): All {
    val normalized = ensureRectangular(document)
    val removeCount = count.coerceAtMost(normalized.columnSettings.size)
    val nextColumns = normalized.columnSettings.dropLast(removeCount)
    val nextRows = normalized.module.map { row ->
        row.copy(columns = row.columns.take(nextColumns.size))
    }
    return creatorDocumentResult(
        document = normalized.copy(columnSettings = nextColumns, module = nextRows),
        statusText = "$removeCount Header entfernt."
    )
}

internal fun fillCreatorSelection(
    document: All,
    range: TabularCellRange,
    targetBottomRow: Int?
): All? {
    val normalized = ensureRectangular(document)
    val sourceValue = normalized.module.getOrNull(range.topRow)?.columns?.getOrNull(range.leftCol).orEmpty()
    if (sourceValue.isBlank()) return null
    val width = range.rightCol - range.leftCol + 1
    val bottomRow = maxOf(range.bottomRow, targetBottomRow ?: range.bottomRow)
    val targetRange = TabularCellRange(range.topRow, range.leftCol, bottomRow, range.rightCol)
    val height = targetRange.bottomRow - targetRange.topRow + 1
    return creatorDocumentResult(
        document = applyCreatorBlock(
            document = ensureCreatorCapacity(
                document = normalized,
                requiredCols = targetRange.rightCol + 1,
                requiredRows = targetRange.bottomRow + 1
            ),
            startRow = targetRange.topRow,
            startCol = targetRange.leftCol,
            block = List(height) { List(width) { sourceValue } }
        ),
        statusText = "Bereich gefüllt."
    )
}

internal fun parseDelimitedBlock(text: String): List<List<String>> {
    val normalized = text.replace("\r\n", "\n").replace('\r', '\n')
    val lines = normalized.split('\n').filter { it.isNotEmpty() }
    if (lines.isEmpty()) return emptyList()
    val delimiter = when {
        lines.first().contains('\t') -> '\t'
        lines.first().contains(';') -> ';'
        else -> ','
    }
    return lines.map { row ->
        row.split(delimiter).map { it.trim('"') }
    }
}
